// How the app takes over #root, and why what a visitor typed survives it
// (#5199).
//
// A prerendered public page (#2567) paints its own markup, forms included,
// before the bundle arrives, and stays as painted until the page's code has
// loaded (#4474). The browser lets a visitor use those fields meanwhile. On a
// throttled phone (slow 4G, 4x CPU, live site, 2026-10-23) the Where do I
// stand form painted at ~0.4 s and became the app's at ~6.2 s; Astra's box on
// the landing page at ~0.4 s and ~3.6 s. Hydration then set every controlled
// field back to its component's state: the grade went back to 12th, the GPA
// and home state went blank, Astra's box emptied, and Check said "Enter your
// unweighted GPA" to someone who had. A Check pressed in that window did the
// form's native GET and reloaded the page.
//
// So: index.html holds a submit until the app is listening
// (window.__stratiaEarly, `forms` and `off`); takeOver() reads what the
// painted fields hold just before hydrating; once the app has committed, each
// field the app set back to its painted value gets the visitor's value again,
// through the native setter and the event React listens for, so the
// component's own onChange runs and its state holds it. Then the held
// submits run, through the component's own onSubmit.
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';

export const EARLY_KEY = '__stratiaEarly';

// Fields a value can be replayed into. Not a checkbox, radio, file or
// password: nothing on a painted public page has one, and each needs its own
// handling a guess would get wrong.
const TEXT_TYPES = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'number']);

function replayable(el) {
    if (el.disabled || el.readOnly) return false;
    const tag = el.tagName;
    if (tag === 'TEXTAREA') return true;
    if (tag === 'SELECT') return !el.multiple;
    return tag === 'INPUT' && TEXT_TYPES.has((el.getAttribute('type') || '').toLowerCase());
}

// The value the markup painted: what React will set the field back to.
function paintedValue(el) {
    if (el.tagName !== 'SELECT') return el.defaultValue;
    const opts = Array.from(el.options);
    const chosen = opts.find((o) => o.defaultSelected) || opts[0];
    return chosen ? chosen.value : '';
}

/** Every painted field the visitor changed: [{el, value, painted}]. */
export function readEarlyInput(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll('input, textarea, select'))
        .filter(replayable)
        .map((el) => ({ el, value: el.value, painted: paintedValue(el) }))
        .filter((f) => f.value !== f.painted);
}

/** Put each value back through the field's own handlers; returns how many. */
export function replayEarlyInput(fields) {
    let n = 0;
    for (const { el, value: before, painted } of fields) {
        if (!el.isConnected) continue;
        // Hydration keeps what is on screen while the component's state stays
        // the painted value, and that component's next render writes the
        // painted value back. So: a field no longer showing the painted value
        // shows the visitor's latest (they may have typed on during
        // hydration); one showing it again gets what was read before.
        const value = el.value !== painted ? el.value : before;
        const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value')?.set;
        if (!setter) continue;
        // The route's Suspense boundary hydrates after the root commits, so
        // the field may not be the app's yet. An input event makes React
        // hydrate it now (it hydrates what a person interacts with first);
        // with the value unchanged, React sees no change in it.
        el.dispatchEvent(new Event('input', { bubbles: true }));
        // React fires onChange only when the field's value differs from the
        // one it last saw, which it tracks through the element's own value
        // property. Show it the painted value that way, then put the visitor's
        // in through the prototype's setter, which it does not see.
        el.value = painted;
        setter.call(el, value);
        el.dispatchEvent(new Event(el.tagName === 'SELECT' ? 'change' : 'input', { bubbles: true }));
        n += 1;
    }
    return n;
}

/** Stop index.html's hold and run the submits it held, in order. */
export function releaseHeldSubmits(win = window) {
    const early = win[EARLY_KEY];
    if (!early) return 0;
    early.off = true;
    const forms = (early.forms || []).splice(0);
    let n = 0;
    for (const form of forms) {
        if (!form || !form.isConnected) continue;
        // A submit event, not requestSubmit(): the component's onSubmit runs
        // and prevents the default, and a form the app does not handle is
        // never sent natively by a replay.
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        n += 1;
    }
    return n;
}

// Runs once the app's first commit is done: a parent's effect runs after
// every child's, so each field's component is mounted and listening.
function Committed({ onCommit, children }) {
    useEffect(() => { onCommit(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
    return children;
}

/**
 * Hydrate the painted markup (or render fresh when there is none), then give
 * back what the visitor typed and run what they submitted meanwhile.
 */
export function takeOver(root, app, { hydrate, onRecoverableError, win = window } = {}) {
    const fields = hydrate ? readEarlyInput(root) : [];
    let done = false;
    const onCommit = () => {
        if (done) return;
        done = true;
        // A task later, outside React's commit: an event dispatched from
        // inside an effect cannot make React hydrate the field it lands on,
        // and is dropped. Each replayed value renders before the held
        // submits run, a task after that.
        win.setTimeout(() => {
            replayEarlyInput(fields);
            win.setTimeout(() => releaseHeldSubmits(win), 0);
        }, 0);
    };
    const tree = <Committed onCommit={onCommit}>{app}</Committed>;
    if (hydrate) return ReactDOM.hydrateRoot(root, tree, { onRecoverableError });
    root.textContent = '';
    const r = ReactDOM.createRoot(root);
    r.render(tree);
    return r;
}
